import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";

import type { RefillStep } from "@/lib/api/types";

// TrajectoryChart plots pre/post pH + EC across a refill session's steps.
// Two Y-axes: pH on the left, EC on the right. Voided steps are excluded
// from the line plot so the trajectory reflects what actually contributed.
// Hover surfaces every datum for the highlighted step.

type Datum = {
  index: number;
  label: string;
  pre_ph?: number;
  post_ph?: number;
  pre_ec?: number;
  post_ec?: number;
};

export function TrajectoryChart({ steps }: { steps: RefillStep[] }) {
  const data = steps
    .filter((s) => !s.voided_at)
    .map<Datum>((s) => ({
      index: s.step_index,
      label: `#${s.step_index} ${s.phase}`,
      pre_ph: s.pre_ph,
      post_ph: s.post_ph,
      pre_ec: s.pre_ec,
      post_ec: s.post_ec,
    }));

  if (data.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        No active steps recorded yet — trajectory will populate as readings come in.
      </p>
    );
  }

  return (
    <div className="h-72 w-full rounded-md border p-2">
      <ResponsiveContainer>
        <LineChart data={data} margin={{ top: 8, right: 16, bottom: 8, left: 0 }}>
          <CartesianGrid strokeOpacity={0.2} />
          <XAxis dataKey="label" tick={{ fontSize: 11 }} interval={0} angle={-15} dy={6} height={50} />
          <YAxis
            yAxisId="ph"
            domain={[4.5, 8.5]}
            tick={{ fontSize: 11 }}
            label={{ value: "pH", angle: -90, position: "insideLeft", style: { fontSize: 11 } }}
          />
          <YAxis
            yAxisId="ec"
            orientation="right"
            domain={[0, 3]}
            tick={{ fontSize: 11 }}
            label={{ value: "EC", angle: 90, position: "insideRight", style: { fontSize: 11 } }}
          />
          <Tooltip
            wrapperClassName="text-xs"
            labelStyle={{ fontWeight: 500 }}
          />
          <Legend wrapperStyle={{ fontSize: 11 }} />
          <Line yAxisId="ph" type="monotone" dataKey="pre_ph" stroke="#8b5cf6" strokeDasharray="3 3" dot={{ r: 2 }} name="pre pH" />
          <Line yAxisId="ph" type="monotone" dataKey="post_ph" stroke="#7c3aed" dot={{ r: 3 }} name="post pH" />
          <Line yAxisId="ec" type="monotone" dataKey="pre_ec" stroke="#10b981" strokeDasharray="3 3" dot={{ r: 2 }} name="pre EC" />
          <Line yAxisId="ec" type="monotone" dataKey="post_ec" stroke="#059669" dot={{ r: 3 }} name="post EC" />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}
